/**
 * 「パルの おてつだい」= 詰まり検知と応援(docs/07_learning_support.md §7.4)。
 *
 * docs/07 §7.4:
 *   「**検知(クライアント・ローカルルール・無料)**: 問題表示からの無操作時間と誤答回数で
 *     段階を上げる。15秒無操作 → L0 励まし / 30秒無操作 or 誤答1回 → L1 着眼点 /
 *     45秒無操作 or 誤答2回 → L2 考え方(+かいせつを もういちど みる)」
 *   「**タイピング画面**: 一定時間無入力で**パルの応援吹き出し**(定型・ローカルのみ)」
 *
 * ここは**描画にも通信にも触れない純関数**だけを置く(状態機械 + 文言)。
 * 画面側(`ui/learn/useLearnStuck.ts` / `ui/PlayScreen.tsx`)は
 * 「時間を進める」「操作があったと伝える」だけを担当する。
 *
 * ## レベルの上がりかた(下がらない)
 * レベルは**同じ問題の中では絶対に下がらない**。
 * 「ヒントが出たのに、指を動かしたら消えた」は子どもを混乱させるので、
 * 操作でリセットするのは**無操作時間だけ**にしてある。
 * 問題が変わったときだけ、そのときの「続けてまちがえた回数」に応じた
 * 出発レベル(`baseStuckLevel`)から数え直す。
 *
 * ## 誤答は「続けてまちがえた回数」で数える
 * まなびは1問1答(押した瞬間に判定)なので、**同じ問題を2回まちがえられない**。
 * そこで docs の「誤答M回」は**セッションの中で続けてまちがえた回数**として読む
 * (正解したら 0 に戻る)。1問まちがえたら つぎの問題では着眼点から始まり、
 * 2問続けてまちがえたら 考え方 + 「こたえを みる」が出る、という動きになる。
 */

/** 詰まりの段階(docs/07 §3.2 のラダー。`null` = まだ何も出していない)。 */
export type StuckLevel = 0 | 1 | 2;

/** 段階のしきい値。 */
export const STUCK_LADDER = {
  /**
   * 無操作でレベルが上がるまでの時間(ms)。添字がそのままレベル。
   * L0 = 励まし / L1 = 着眼点 / L2 = 考え方。
   */
  idleMs: [15_000, 30_000, 45_000] as readonly number[],
  /** 続けてまちがえた回数 → 出発レベル(この回数**以上**でそのレベルから始まる)。 */
  wrongForFocus: 1,
  wrongForApproach: 2,
  /** 「こたえを みる」を出すレベル(docs/07 §7.4「L2以降」)。 */
  revealFromLevel: 2 as StuckLevel,
  /** 画面側の刻み(ms)。細かくしても意味が無く、粗いと段階が遅れる。 */
  tickMs: 500,
} as const;

/** 1問ぶんの検知の状態。 */
export interface StuckState {
  /** いまの問題を表す鍵(変わったら数え直す)。 */
  readonly key: string;
  /** いま出している段階(`null` = 何も出していない)。 */
  readonly level: StuckLevel | null;
  /** 最後の操作からの経過(ms)。 */
  readonly idleMs: number;
  /** 続けてまちがえた回数(問題が変わるときに外から渡される)。 */
  readonly wrongStreak: number;
}

export type StuckEvent =
  /** 新しい問題が出た(または解説・◯✕で中断していた検知を仕切り直す)。 */
  | { readonly type: 'question'; readonly key: string; readonly wrongStreak: number }
  /** 子どもが何か操作した(タップ・キー)。**時間だけ**リセットする。 */
  | { readonly type: 'activity' }
  /** 時間が進んだ。 */
  | { readonly type: 'tick'; readonly deltaMs: number };

/** まだ何も出していない状態。 */
export const IDLE_STUCK: StuckState = { key: '', level: null, idleMs: 0, wrongStreak: 0 };

/** 続けてまちがえた回数から決まる**出発レベル**(docs/07 §7.4 の「誤答1回 / 2回」)。 */
export function baseStuckLevel(wrongStreak: number): StuckLevel | null {
  if (wrongStreak >= STUCK_LADDER.wrongForApproach) return 2;
  if (wrongStreak >= STUCK_LADDER.wrongForFocus) return 1;
  return null;
}

/** 無操作時間から決まるレベル(しきい値を下から順に見る)。 */
export function idleStuckLevel(idleMs: number, delays: readonly number[]): StuckLevel | null {
  let level: StuckLevel | null = null;
  for (let index = 0; index < delays.length; index += 1) {
    const threshold = delays[index];
    if (threshold !== undefined && idleMs >= threshold) level = index as StuckLevel;
  }
  return level;
}

/** 2つのレベルの高いほう(`null` は「まだ無い」= いちばん低い)。 */
function higher(a: StuckLevel | null, b: StuckLevel | null): StuckLevel | null {
  if (a === null) return b;
  if (b === null) return a;
  return (a >= b ? a : b) as StuckLevel;
}

/**
 * 状態機械。**レベルは同じ問題の中で下がらない**(higher しか使わない)。
 *
 * @param delays 無操作のしきい値(テスト・E2E で短縮できるように引数にしてある)
 */
export function stuckReduce(
  state: StuckState,
  event: StuckEvent,
  delays: readonly number[] = STUCK_LADDER.idleMs,
): StuckState {
  switch (event.type) {
    case 'question': {
      // 同じ問題を出し直しただけ(再レンダー)なら何も変えない。
      if (event.key === state.key && event.wrongStreak === state.wrongStreak) return state;
      return {
        key: event.key,
        level: baseStuckLevel(event.wrongStreak),
        idleMs: 0,
        wrongStreak: event.wrongStreak,
      };
    }
    case 'activity':
      // 手が動いたら時間だけ戻す。出したヒントは引っ込めない。
      return state.idleMs === 0 ? state : { ...state, idleMs: 0 };
    case 'tick': {
      const idleMs = state.idleMs + Math.max(0, event.deltaMs);
      const level = higher(state.level, idleStuckLevel(idleMs, delays));
      if (level === state.level && idleMs === state.idleMs) return state;
      return { ...state, idleMs, level };
    }
    default:
      return state;
  }
}

/** 「こたえを みる」を出してよいか(docs/07 §7.4「L2以降」)。 */
export function canRevealAnswer(level: StuckLevel | null): boolean {
  return level !== null && level >= STUCK_LADDER.revealFromLevel;
}

/**
 * `?stuckMs=600` を読む(E2E・デバッグ用)。無ければ `null`。
 *
 * 45秒待つ E2E は書けないので、**しきい値を丸ごと縮められる**ようにしてある。
 * 与えた値が L0 になり、L1・L2 は その2倍・3倍(本番と同じ比)。
 */
export function readStuckDelays(search: string): readonly number[] | null {
  const match = /[?&]stuckMs=(\d+)/.exec(search);
  const base = match?.[1] === undefined ? Number.NaN : Number(match[1]);
  if (!Number.isFinite(base) || base <= 0) return null;
  return [base, base * 2, base * 3];
}

// ── ことば(ひらがな主体。CLAUDE.md ルール5)──────────────────────────

/**
 * レベル0の励まし(定型ローテ・LLM不要。docs/07 §7.4)。
 *
 * **ヒントではない**。「見ているよ」「あせらなくていいよ」だけを伝える
 * (ここで解きかたを言ってしまうと、考える時間を奪ってしまう)。
 */
export const STUCK_CHEERS: readonly string[] = [
  'ゆっくりで だいじょうぶ。よく みて みよう!',
  'んー、むずかしいね。いっしょに かんがえよう!',
  'あせらなくて いいよ。パルが ここに いるよ!',
  'もういちど よんで みると、みえて くるかも!',
];

/** 励ましを1つ選ぶ(同じ問題では変わらないように鍵から決める)。 */
export function stuckCheerFor(seed: string): string {
  return STUCK_CHEERS[stableIndex(seed, STUCK_CHEERS.length)] as string;
}

/** タイピングで応援を出すまでの無入力時間(ms)。docs/07 §7.4。 */
export const TYPING_CHEER_MS = 20_000;

/**
 * タイピングの応援(定型ローテ・ローカルのみ)。
 *
 * 「つぎに打つキー」のヒントは既存の指ガイドが担っているので、
 * ここは**手が止まっている子の背中を押すだけ**にする(打ちかたは言わない)。
 */
export const TYPING_CHEERS: readonly string[] = [
  'あわてなくて いいよ。ゆっくり さがそう!',
  'キーの いろを みると みつかるよ!',
  'ゆびの かたちは そのままで だいじょうぶ!',
  'いっぽずつで いいんだ。パルが みてるよ!',
];

/** 何回めの応援かで選ぶ(ずっと同じ文が出ないように)。 */
export function typingCheerAt(index: number): string {
  const size = TYPING_CHEERS.length;
  const at = ((Math.trunc(index) % size) + size) % size;
  return TYPING_CHEERS[at] as string;
}

/** 詰まりヒントのボタン・見出しの文言。 */
export const STUCK_LABELS = {
  /** L2 で出す「解説パートをもう一度見る」。 */
  lesson: '📖 かいせつを もういちど みる',
  /** L2 以降で出す「こたえを みる」(docs/07 §3.2 の主導権)。 */
  reveal: '💡 こたえを みる',
  /** 絵のヒント(スケッチ)を しまう。じゃまなら いつでも 消せる。 */
  closeSketch: '✕ えを とじる',
} as const;

/** 文字列から決まる添字(ランダムにすると再レンダーで文が踊る)。 */
function stableIndex(seed: string, size: number): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return size === 0 ? 0 : hash % size;
}
